import { useEffect } from 'react'
import { Box, Input, Title } from '@holbonkit/ui'
import { Controller, useForm } from 'react-hook-form'

import { MaskedInput } from '@/shared/ui'

import { sanitizeString } from '../../lib/capitalize-first-letter'
import type { TStepFormProps } from '../../lib/step-form-props'
import type { TCitizenship } from '../../model/types'

import { DocumentsBottomSheet } from './documents-bottom-sheet'
import type { TDocumentsFormNotRF, TDocumentsFormRF } from './types'
import { documentsValidator } from './validator'

import styles from './documents.module.scss'

export const DOCUMENTS_FORM_ID = 'smz-documents-form'

const EMPTY_DOCUMENTS_RF: TDocumentsFormRF = {
    passportNumber: '',
    dateOfIssue: '',
    issuedBy: '',
    unitCode: '',
    placeOfBirth: '',
    inn: '',
}

const EMPTY_DOCUMENTS_NOT_RF: TDocumentsFormNotRF = {
    type: '',
    documentNumber: '',
    issuedBy: '',
    dateOfIssue: '',
    inn: '',
}

type TDocumentsProps = TStepFormProps<TDocumentsFormNotRF | TDocumentsFormRF> & {
    citizenship: TCitizenship
}

export const Documents = ({
    citizenship,
    defaultValues,
    formId,
    onSubmit,
    onValidityChange,
}: TDocumentsProps) => {
    const {
        clearErrors,
        control,
        handleSubmit,
        reset,
        formState: { isValid },
    } = useForm<TDocumentsFormNotRF | TDocumentsFormRF>({
        defaultValues: citizenship === 'rf' ? EMPTY_DOCUMENTS_RF : EMPTY_DOCUMENTS_NOT_RF,
        mode: 'onTouched',
        reValidateMode: 'onChange',
    })

    useEffect(() => {
        reset(defaultValues ?? (citizenship === 'rf' ? EMPTY_DOCUMENTS_RF : EMPTY_DOCUMENTS_NOT_RF))
    }, [defaultValues, reset, citizenship])

    useEffect(() => {
        onValidityChange?.(isValid)
    }, [isValid, onValidityChange])

    return (
        <form noValidate id={formId} onSubmit={handleSubmit(onSubmit)}>
            <Box className={styles['documents']} pb={4} pt={6} px={4}>
                <Title variant='header-24-bold'>Документы</Title>
                {citizenship === 'not_rf' && (
                    <Controller
                        control={control}
                        name='type'
                        rules={documentsValidator.type}
                        render={({ field, fieldState }) => (
                            <DocumentsBottomSheet
                                errorText={fieldState.error?.message}
                                name={field.name}
                                value={field.value}
                                onBlur={field.onBlur}
                                onChange={field.onChange}
                            />
                        )}
                    />
                )}
                <Controller
                    control={control}
                    name={citizenship === 'not_rf' ? 'documentNumber' : 'passportNumber'}
                    render={({ field, fieldState }) =>
                        citizenship === 'not_rf' ?
                            <Input
                                errorText={fieldState.error?.message}
                                label={'Номер документа'}
                                maxLength={30}
                                name={field.name}
                                value={field.value}
                                onBlur={field.onBlur}
                                onChange={(event) => {
                                    field.onChange(event.target.value.trim().toUpperCase())
                                }}
                            />
                        :   <MaskedInput
                                data-test-id='documents__passport-number'
                                errorText={fieldState.error?.message}
                                label='Серия и номер паспорта'
                                name={field.name}
                                preset='passportNumber'
                                value={field.value}
                                onBlur={field.onBlur}
                                onChange={field.onChange}
                                onFocus={() => {
                                    clearErrors('passportNumber')
                                }}
                            />
                    }
                    rules={
                        citizenship === 'not_rf' ?
                            documentsValidator.number
                        :   documentsValidator.passportNumber
                    }
                />
                {citizenship === 'not_rf' && (
                    <Controller
                        control={control}
                        name='issuedBy'
                        rules={documentsValidator.issuedBy}
                        render={({ field, fieldState }) => (
                            <Input
                                errorText={fieldState.error?.message}
                                label='Орган, выдавший документ'
                                size='lg'
                                type='text'
                                {...field}
                                onChange={(event) => {
                                    field.onChange(sanitizeString(event.target.value))
                                }}
                            />
                        )}
                    />
                )}
                <Controller
                    control={control}
                    name='dateOfIssue'
                    rules={documentsValidator.dateOfIssue}
                    render={({ field, fieldState }) => (
                        <MaskedInput
                            data-test-id='documents__date-of-issue'
                            errorText={fieldState.error?.message}
                            label='Дата выдачи'
                            name={field.name}
                            preset='date'
                            value={field.value}
                            onBlur={field.onBlur}
                            onChange={field.onChange}
                            onFocus={() => {
                                clearErrors('dateOfIssue')
                            }}
                        />
                    )}
                />
                {citizenship === 'rf' && (
                    <>
                        <Controller
                            control={control}
                            name='issuedBy'
                            rules={documentsValidator.issuedBy}
                            render={({ field, fieldState }) => (
                                <Input
                                    errorText={fieldState.error?.message}
                                    label='Кем выдан'
                                    maxLength={150}
                                    size='lg'
                                    type='text'
                                    {...field}
                                    onChange={(event) => {
                                        field.onChange(sanitizeString(event.target.value))
                                    }}
                                    onFocus={() => {
                                        clearErrors('issuedBy')
                                    }}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name='unitCode'
                            rules={documentsValidator.unitCode}
                            render={({ field, fieldState }) => (
                                <MaskedInput
                                    data-test-id='documents__unit-code'
                                    errorText={fieldState.error?.message}
                                    label='Код подразделения'
                                    name={field.name}
                                    preset='unitCode'
                                    value={field.value}
                                    onBlur={field.onBlur}
                                    onChange={field.onChange}
                                    onFocus={() => {
                                        clearErrors('unitCode')
                                    }}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name='placeOfBirth'
                            rules={documentsValidator.placeOfBirth}
                            render={({ field, fieldState }) => (
                                <Input
                                    errorText={fieldState.error?.message}
                                    label='Место рождения как в паспорте'
                                    maxLength={50}
                                    size='lg'
                                    type='text'
                                    {...field}
                                    onChange={(event) => {
                                        field.onChange(sanitizeString(event.target.value))
                                    }}
                                    onFocus={() => {
                                        clearErrors('placeOfBirth')
                                    }}
                                />
                            )}
                        />
                    </>
                )}
                {citizenship === 'rf' && (
                    <Controller
                        control={control}
                        name='placeOfBirth'
                        rules={documentsValidator.placeOfBirth}
                        render={({ field, fieldState }) => (
                            <Input
                                errorText={fieldState.error?.message}
                                label='Место рождения как в паспорте'
                                maxLength={50}
                                size='lg'
                                type='text'
                                {...field}
                                onChange={(event) => {
                                    field.onChange(sanitizeString(event.target.value))
                                }}
                            />
                        )}
                    />
                )}
                <Controller
                    control={control}
                    name='inn'
                    rules={documentsValidator.inn}
                    render={({ field, fieldState }) => (
                        <MaskedInput
                            data-test-id='documents__inn'
                            errorText={fieldState.error?.message}
                            label='ИНН'
                            name={field.name}
                            preset='inn'
                            value={field.value}
                            onBlur={field.onBlur}
                            onChange={field.onChange}
                            onFocus={() => {
                                clearErrors('inn')
                            }}
                        />
                    )}
                />
            </Box>
        </form>
    )
}
